import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Button, Modal as BootstrapModal, ModalHeader, ModalBody} from 'reactstrap';

import Modal from '../Modal.jsx';

import api from '../../../../api.js';
import {isAdmin} from '../../../../shared/services/userService.js';

import './RemoveModal.scss';

class RemoveModal extends Modal {
  static propTypes = {
    isAdmin: PropTypes.bool,
    onHardDelete: PropTypes.func,
    onLongTermContractUpdated: PropTypes.func,
    onSoftDelete: PropTypes.func,
  };

  static defaultProps = {
    isAdmin: false,
    onHardDelete: () => {},
    onLongTermContractUpdated: () => {},
    onSoftDelete: () => {},
  };

  constructor(props) {
    super(props);

    this.state = {
      hardDeleteConfirmation: false,
      longTermContractConfirmation: false,
      longTermContractUpdated: false,
      loading: false,
      error: false
    };
  }

  renderTitle = () => {
    if (this.props.isAdmin) {
      return this.state.hardDeleteConfirmation ? 'Hard delete' : 'Supprimer';
    }

    if (this.state.longTermContractUpdated || this.state.longTermContractConfirmation) {
      return 'Je suis en CDI';
    }

    return 'Supprimer '.concat(this.state.hardDeleteConfirmation ? 'quand même' : 'mon compte');
  };

  renderText = () => {
    if (this.props.isAdmin) {
      return this.state.harDeleteConfirmation ?
        <p>Attention, le hard delete supprime définitivement les données de l'utilisateur</p> :
        <p>Attention, la suppression d'un utilisateur est irréversible</p>;
    }
    if (this.state.hardDeleteConfirmation) {
      return <p>Vous confirmez vouloir supprimer votre compte ?</p>;
    }
    if (this.state.longTermContractConfirmation) {
      return <p>Vous confirmez être actuellement en CDI ?</p>;
    }
    if (this.state.longTermContractUpdated) {
      return <Fragment>
        <p>
          Votre profil a bien été qualifié en CDI et est désormais masqué sur notre plateforme. Vous ne recevrez plus
          de communication directe (mail & SMS) de la part de Twineeds. Nous vous recontacterons une fois par an
          maximum afin d'actualiser votre situation.
        </p>
        <p className="text-center">À bientôt !</p>
      </Fragment>;
    }

    return <Fragment>
      <p>
        La suppression de votre compte entraînera la suppression totale de vos données et par conséquent, Twineeds
        sera dans l'incapacité de vous recontacter. Vous devrez également recommencer le processus d'inscription dans
        son intégralité si vous veniez être à l'écoute de nouvelles opportunités dans le futur.
      </p>
      <p>
        Vous pouvez modifier les notifications afin de modifier la fréquence des envois de mail et SMS ou indiquer que
        vous êtes en CDI.
      </p>
      <p>
        Si vous nous indiquez être en CDI, votre profil sera uniquement visible pour l'équipe Twineeds et vous ne
        recevrez plus de communications de notre part. Nous ne vous contacterons une seule fois par an afin d'actualiser
        votre situation et ainsi réactiver votre profil si besoin.
      </p>
    </Fragment>;
  };

  renderActions = () => {
    if (this.state.longTermContractUpdated) {
      return <Button color="primary" onClick={this.props.closeModal}>Fermer</Button>;
    }

    const cancelLabel = (
      !this.props.isAdmin && !this.state.hardDeleteConfirmation && !this.state.longTermContractConfirmation
    ) ? 'Je veux garder mon compte' : 'Annuler';
    const cancelAction = (this.state.longTermContractConfirmation || this.state.hardDeleteConfirmation) ?
      this.exitConfirmation : this.props.closeModal;
    const actions = [{color: 'primary', label: cancelLabel, onClick: cancelAction}];

    if (!this.state.longTermContractConfirmation && !this.state.hardDeleteConfirmation && !this.props.isAdmin) {
      actions.push({color: 'primary', label: 'Je suis en CDI', onClick: this.askLongTermContractConfirmation});
    }
    if (this.state.longTermContractConfirmation) {
      actions.push({color: 'primary', label: 'Je suis en CDI', onClick: this.updateLongTermContract});
    }
    if (this.state.hardDeleteConfirmation) {
      actions.push({
        color: 'danger',
        label: 'Supprimer '.concat(this.props.isAdmin ? 'quand même' : 'mon compte'),
        onClick: () => this.removeUser(false)
      });
    }
    if (!this.state.longTermContractConfirmation && !this.state.hardDeleteConfirmation) {
      if (this.props.isAdmin) {
        actions.push({color: 'warning', label: 'Soft delete', onClick: () => this.removeUser(true)});
      }

      actions.push({
        color: 'danger',
        label: this.props.isAdmin ? 'Hard delete' : 'Supprimer mon compte',
        onClick: this.askHardDeleteConfirmation
      });
    }

    return actions.map(
      action => <Button
        color={action.color}
        onClick={action.onClick ? action.onClick : this.props.closeModal}
        key={action.label}
      >
        {action.label}
      </Button>
    );
  };

  exitConfirmation = () => {
    this.setState({
      hardDeleteConfirmation: false,
      longTermContractConfirmation: false,
    });
  };

  askLongTermContractConfirmation = () => {
    this.setState({longTermContractConfirmation: true});
  };

  askHardDeleteConfirmation = () => {
    this.setState({hardDeleteConfirmation: true});
  };

  updateLongTermContract = async () => {
    try {
      await this.props.onLongTermContractUpdated();
    } catch(e) {
      console.warn(e);
    }
    this.setState({
      longTermContractConfirmation: false,
      longTermContractUpdated: true
    });
  };

  removeUser = async (softDelete) => {
    try {
      await api.users.remove({
        user: this.props.user.id,
      }, {
        qs: {
          softdelete: softDelete,
        }
      })
    } catch(e) {
      console.warn(e);
    } finally {
      if (softDelete) {
        await this.props.onSoftDelete({gray: true, email: null});
      } else {
        this.props.onHardDelete();
      }
      this.props.closeModal();
    }
  };

  render() {
    const {closeModal, isAdmin, ...props} = this.props;

    const modalProps = {};

    if (!this.state.loading) {
      modalProps.toggle = closeModal;
    }

    const modalSize = isAdmin ||
      this.state.longTermContractConfirmation ||
      this.state.hardDeleteConfirmation ? null : 'lg';

    return <BootstrapModal isOpen={props.isOpen} size={modalSize} toggle={closeModal}>
      <ModalHeader toggle={closeModal}>{this.renderTitle()}</ModalHeader>
      <ModalBody>
        <div className="text-center mb-4">{this.renderText()}</div>
        <div className="d-flex align-items-center justify-content-around ctas">{this.renderActions()}</div>
      </ModalBody>
    </BootstrapModal>;
  }
}

export default connect(
  state => ({
    isAdmin: isAdmin(state.currentUser.user, state.universe.currentUniverse),
  })
)(RemoveModal);